import { removeOrganizationMembers } from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";

interface IProps {
    /**
     * The organization id to remove the user from.
     */
    organizationId: number;
    /**
     * The user id to remove from the organization.
     */
    userId: number;
}

/**
 * A dialog for removing a single clause from a clause checklist.
 */
export const RemoveUserFromOrganizationDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<IProps>) => {
    const { organizationId, userId } = payload;

    const queryClient = useQueryClient();
    const { mutate, isPending, isSuccess, isError } = useMutation({
        mutationFn: () => removeOrganizationMembers(organizationId, [userId]),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-organization-members"],
            });
        },
    });

    const title = `Remove User`;
    const description = `Are you sure you want to remove this user from the organization?`;
    const destructiveAction = title;
    const cancelAction = `No, Keep User`;

    const errorMsg = `The user could not be removed. Please try again later.`;
    const successMsg = `The user has been removed from the organization.`;

    return (
        <Dialog open={open} onClose={() => onClose()}>
            <DialogContent sx={{ p: 5 }}>
                <Stack alignItems={"center"} spacing={6}>
                    {isPending && <CircularProgress sx={{ fontSize: 53 }} />}
                    {!isPending && !isSuccess && (
                        <ErrorOutlineIcon color="error" sx={{ fontSize: 53 }} />
                    )}
                    {isSuccess && (
                        <CheckCircleOutlineIcon
                            color="success"
                            sx={{ fontSize: 53 }}
                        />
                    )}
                    <Stack alignItems={"center"}>
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            textTransform={"capitalize"}
                        >
                            {isError && "Error"}
                            {isSuccess && "Success"}
                            {!isSuccess && !isError && title}
                        </Typography>
                        <Typography>
                            {isError && errorMsg}
                            {isSuccess && successMsg}
                            {!isSuccess && !isError && description}
                        </Typography>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{ px: 5, pb: 5, justifyContent: "center", gap: 3 }}
            >
                {!isSuccess && !isError && (
                    <>
                        <Button
                            onClick={() => onClose()}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                color: "text.primary",
                            }}
                            disabled={isPending}
                        >
                            {cancelAction}
                        </Button>
                        <Button
                            onClick={() => mutate()}
                            variant="contained"
                            color="error"
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                textTransform: "capitalize",
                            }}
                            disabled={isPending}
                        >
                            {destructiveAction}
                        </Button>
                    </>
                )}
                {(isSuccess || isError) && (
                    <Button
                        onClick={() => onClose()}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            px: 2.5,
                            color: "text.primary",
                        }}
                        disabled={isPending}
                        fullWidth
                    >
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
